import type { Auswahloption, Schritt } from "@fbs/funnel-schema";
import { spaltenFuer } from "../raster";

import { alsListe, alsText, type SchrittProps } from "./gemeinsam";

type Einfachauswahl = Extract<Schritt, { type: "single_choice" }>;
type Mehrfachauswahl = Extract<Schritt, { type: "multiple_choice" }>;

/**
 * Die Auswahlkarte.
 *
 * Bewusst ein Button und keine gestylte Radiobox: Die gesamte Fläche ist das
 * Ziel, nicht ein kleines Kästchen daneben. Auf dem Handy wird mit dem Daumen
 * getippt, und der trifft eine Karte, kein 20-Pixel-Quadrat.
 *
 * Die Rolle wird trotzdem gesetzt, damit ein Screenreader die Gruppe als das
 * ansagt, was sie ist.
 */
function Karte({
  option,
  gewaehlt,
  mehrfach,
  istStart,
  onTippen,
}: {
  option: Auswahloption;
  gewaehlt: boolean;
  mehrfach: boolean;
  /** Erste Frage des Funnels - siehe SchrittProps.istStart. */
  istStart: boolean;
  onTippen: () => void;
}) {
  return (
    <button
      type="button"
      /*
        f-karte--start hat absichtlich keine Regel im Stylesheet.
        Sie ist eine Marke fuer das Messwerkzeug, kein Aussehen.
      */
      class={`f-karte${gewaehlt ? " f-karte--an" : ""}${option.image ? " f-karte--foto" : ""}${istStart ? " f-karte--start" : ""}`}
      role={mehrfach ? "checkbox" : "radio"}
      aria-checked={gewaehlt}
      onClick={onTippen}
    >
      {/*
        Bild vor Zeichen.

        Ein Bild ist bei manchen Fragen die eigentliche Antwort: "Walmdach"
        sagt vielen nichts, das Bild eines Walmdachs schon. Sind beide da,
        gewinnt das Bild; das Zeichen ist der Rückfall, wenn kein Bild vorliegt.
      */}
      {option.image ? (
        <img class="f-karte__bild" src={option.image} alt="" loading="lazy" />
      ) : option.icon ? (
        <span class="f-karte__icon" aria-hidden="true">
          {option.icon}
        </span>
      ) : null}
      <span class="f-karte__text">
        <span class="f-karte__label">{option.label}</span>
        {option.description ? (
          <span class="f-karte__beschreibung">{option.description}</span>
        ) : null}
      </span>
      <span class="f-karte__marke" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="14" height="14">
          <path
            d="M4 10.5 8 14.5 16 6"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

export function EinfachauswahlAnsicht({
  schritt,
  wert,
  istStart,
  onWert,
  onAutoWeiter,
}: SchrittProps<Einfachauswahl>) {
  const gewaehlt = alsText(wert);

  return (
    <div
      class={`f-optionen f-optionen--${schritt.layout}`}
      style={{ "--spalten-wunsch": spaltenFuer(schritt.options.length) }}
      role="radiogroup"
    >
      {schritt.options.map((option) => (
        <Karte
          key={option.value}
          option={option}
          mehrfach={false}
          istStart={istStart}
          gewaehlt={gewaehlt === option.value}
          onTippen={() => {
            onWert(option.value);
            // Die Auswahl bleibt noch einen Moment sichtbar, dann geht es von
            // selbst weiter. Ohne diese Pause wirkt der Wechsel wie ein
            // Aussetzer, und der Nutzer weiß nicht, was er getroffen hat.
            onAutoWeiter();
          }}
        />
      ))}
    </div>
  );
}

export function MehrfachauswahlAnsicht({
  schritt,
  wert,
  istStart,
  onWert,
}: SchrittProps<Mehrfachauswahl>) {
  const gewaehlt = alsListe(wert);

  const umschalten = (option: Auswahloption) => {
    const drin = gewaehlt.includes(option.value);

    if (drin) {
      onWert(gewaehlt.filter((v) => v !== option.value));
      return;
    }

    // "Nichts davon" hebt alles andere auf, und alles andere hebt es auf.
    // Sonst entstehen Antworten, die sich selbst widersprechen.
    if (option.exclusive) {
      onWert([option.value]);
      return;
    }

    const ausschliessend = new Set(
      schritt.options.filter((o) => o.exclusive).map((o) => o.value),
    );
    const bereinigt = gewaehlt.filter((v) => !ausschliessend.has(v));

    // Ist die Obergrenze erreicht, rückt die älteste Auswahl heraus. Das ist
    // freundlicher als eine Fehlermeldung: Der Tipp bewirkt etwas.
    const grenze = schritt.max_select;
    const platz = grenze !== undefined && bereinigt.length >= grenze;
    const basis = platz ? bereinigt.slice(bereinigt.length - grenze + 1) : bereinigt;

    onWert([...basis, option.value]);
  };

  return (
    <div
      class={`f-optionen f-optionen--${schritt.layout}`}
      style={{ "--spalten-wunsch": spaltenFuer(schritt.options.length) }}
      role="group"
    >
      {schritt.options.map((option) => (
        <Karte
          key={option.value}
          option={option}
          mehrfach
          istStart={istStart}
          gewaehlt={gewaehlt.includes(option.value)}
          onTippen={() => {
            umschalten(option);
          }}
        />
      ))}
    </div>
  );
}
